import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fetchRegion, formatPrice } from '@/lib/region';
import { RechargeProvider } from '@/components/RechargeProvider';
import {
    LayoutDashboard, MessageCircle,
    Users, Wallet, User as UserIcon, LogOut, Gift
} from 'lucide-react';

const NAV = [
    { to: '/app', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/app/chat', icon: MessageCircle, label: 'Chat' },
    { to: '/app/consult', icon: Users, label: 'Experts' },
    { to: '/app/credits', icon: Wallet, label: 'Wallet' },
    { to: '/app/referrals', icon: Gift, label: 'Refer' },
    { to: '/app/profile', icon: UserIcon, label: 'Profile' },
];

export default function AppShell() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [walletInr, setWalletInr] = useState(null);
    // Round 40n — currency-aware wallet pill. Re-resolves on every
    // mount (which happens right after login because `clearRegionCache`
    // forces the cache miss). Falls back to INR until /region/me
    // returns, so existing INR users never flicker.
    const [currency, setCurrency] = useState('INR');

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get('/wallet/balance');
                if (alive) setWalletInr(data?.balance_inr ?? 0);
            } catch { /* not logged in or wallet endpoint not ready */ }
        })();
        fetchRegion()
            .then((r) => { if (alive) setCurrency(r?.currency || 'INR'); })
            .catch(() => { /* default INR */ });
        // Refresh balance every 20s while the shell is mounted so the
        // sidebar tracks recharges + chat ticks reasonably fresh.
        const t = setInterval(async () => {
            try {
                const { data } = await api.get('/wallet/balance');
                if (alive) setWalletInr(data?.balance_inr ?? 0);
            } catch { /* ignore */ }
        }, 20000);
        return () => { alive = false; clearInterval(t); };
    }, [user?.id]);

    const walletLabel = walletInr === null ? '…' : formatPrice(walletInr, currency);

    const onLogout = () => { logout(); navigate('/'); };

    return (
        <RechargeProvider>
        <div className="relative min-h-screen flex flex-col lg:flex-row" data-testid="app-shell">
            {/* Desktop sidebar */}
            <aside className="hidden lg:flex lg:flex-col w-64 border-r border-marigold/15 bg-surface/50 backdrop-blur-sm sticky top-0 h-screen p-6 z-20">
                <div className="mb-10">
                    <NavLink to="/" className="flex items-center gap-2" data-testid="sidebar-logo">
                        <SigilMark />
                        <div>
                            <div className="font-serif text-2xl text-parchment leading-none">AstroConsult</div>
                            <div className="overline mt-1">Vedic · KP Jyotish</div>
                        </div>
                    </NavLink>
                </div>
                <nav className="flex flex-col gap-1 flex-1" aria-label="Primary">
                    {NAV.map(({ to, icon: Icon, label, end }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            data-testid={`nav-${label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 rounded-sm transition-all ${
                                    isActive
                                        ? 'bg-marigold/15 text-parchment border-l-2 border-marigold'
                                        : 'text-slate-400 hover:bg-elevated hover:text-parchment border-l-2 border-transparent'
                                }`
                            }
                        >
                            <Icon size={18} />
                            <span className="text-sm">{label}</span>
                        </NavLink>
                    ))}
                </nav>
                <div className="border-t border-marigold/10 pt-4 space-y-3">
                    <div className="flex items-center gap-2 px-2 py-2">
                        <Wallet size={16} className="text-marigold" />
                        <span className="text-sm text-parchment font-medium" data-testid="sidebar-wallet">
                            {walletLabel}
                        </span>
                    </div>
                    <button
                        onClick={onLogout}
                        data-testid="logout-button"
                        className="flex items-center gap-2 px-2 py-2 text-sm text-slate-400 hover:text-parchment transition-colors w-full"
                    >
                        <LogOut size={16} /> Sign out
                    </button>
                </div>
            </aside>

            {/* Mobile top bar */}
            <header className="lg:hidden sticky top-0 z-30 bg-obsidian/80 backdrop-blur-xl border-b border-marigold/10 flex items-center justify-between px-5 py-4">
                <NavLink to="/" className="flex items-center gap-2" data-testid="mobile-logo">
                    <SigilMark />
                    <span className="font-serif text-xl text-parchment">AstroConsult</span>
                </NavLink>
                <div className="flex items-center gap-2" data-testid="mobile-wallet">
                    <span className="px-3 py-1 rounded-full bg-marigold/10 border border-marigold/30 text-marigold text-sm inline-flex items-center gap-1.5">
                        <Wallet size={14} /> {walletLabel}
                    </span>
                    <NavLink
                        to="/app/profile"
                        data-testid="mobile-profile-link"
                        className={({ isActive }) =>
                            `w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${
                                isActive
                                    ? 'border-marigold bg-marigold/15 text-marigold'
                                    : 'border-marigold/30 text-slate-300 hover:border-marigold/60 hover:text-marigold'
                            }`
                        }
                        aria-label="Profile"
                    >
                        <UserIcon size={16} />
                    </NavLink>
                    <button
                        onClick={onLogout}
                        data-testid="mobile-logout-button"
                        aria-label="Sign out"
                        title="Sign out"
                        className="w-9 h-9 rounded-full flex items-center justify-center border border-marigold/30 text-slate-300 hover:border-marigold/60 hover:text-marigold transition-colors"
                    >
                        <LogOut size={16} />
                    </button>
                </div>
            </header>

            {/* Main content */}
            <main className="flex-1 relative z-10 pb-24 lg:pb-8">
                <Outlet />
            </main>

            {/* Mobile bottom nav */}
            <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-obsidian/90 backdrop-blur-xl border-t border-marigold/10 flex justify-around py-3" data-testid="bottom-nav">
                {NAV.slice(0, 5).map(({ to, icon: Icon, label, end }) => (
                    <NavLink
                        key={to}
                        to={to}
                        end={end}
                        data-testid={`bottom-nav-${label.toLowerCase()}`}
                        className={({ isActive }) =>
                            `flex flex-col items-center gap-1 text-xs ${
                                isActive ? 'text-marigold' : 'text-slate-500'
                            }`
                        }
                    >
                        <Icon size={20} />
                        <span>{label}</span>
                    </NavLink>
                ))}
            </nav>
        </div>
        </RechargeProvider>
    );
}

function SigilMark() {
    return (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden="true">
            <circle cx="16" cy="16" r="13" stroke="#F59E0B" strokeWidth="1" opacity="0.6" />
            <circle cx="16" cy="16" r="5" stroke="#FDE68A" strokeWidth="0.8" />
            <path d="M16 3 L16 29 M3 16 L29 16 M6.7 6.7 L25.3 25.3 M25.3 6.7 L6.7 25.3" stroke="#F59E0B" strokeWidth="0.5" opacity="0.4" />
        </svg>
    );
}
